import { Box, Flex, Image, Text, useDisclosure } from "@chakra-ui/react"
import { FC, useMemo, useRef, useState } from "react"
import { IoInformationCircleSharp } from "react-icons/io5"
import { useSetRecoilState } from "recoil"
import { AuthPageStyling } from "src/context/types"
import { useIsMobileDevice } from "src/util/browser"
import {
  AvatarDimensionality,
  gridSelectedAvatarAtom,
  gridSelectedAvatarDimensionalityAtom
} from "../../../../recoil/atoms/avatars"
import { AvatarConfig } from "../../../../recoil/atoms/avatars/types"
import { checkIsTouchDevice } from "../../../../util"
import { addHitAreaStyle } from "../../../../util/styleUtils"
import ShowCardLoaderOverlay from "../../shows/show-card-loader-overlay"
import { AvatarCardHoverOverlay } from "./avatar-card-hover-overlay-utta"
import AvatarDescriptionModal from "./avatar-description-modal"
import { useClickAway } from "use-click-away"
import { useLocalStorage } from "src/hooks/useLocalStorage"
import useGridPageStyling from "../hooks/useGridPageStyling"
import { LanguageIcon } from "./avatar-language-modal"

export type AvatarCardProps = {
  avatarConfig: AvatarConfig
  branding?: AuthPageStyling
  openRoom: (roomHash: string) => void
  loadingRoomHash?: string | null
  roomHash?: string
  btnText: string
}

export const AvatarCard: FC<AvatarCardProps> = ({
  avatarConfig,
  branding,
  openRoom,
  loadingRoomHash,
  roomHash,
  btnText
}) => {
  const gridBranding = useGridPageStyling()
  const setGridSelectedAvatar = useSetRecoilState(gridSelectedAvatarAtom)

  const sortedLanguageList = useMemo(() => {
    // If language list is empty, then show default EN language
    if (avatarConfig.languages.length === 0)
      return [
        { language: "English", languageCode: "en-GB", languagePosition: 1 }
      ]

    return [...avatarConfig.languages].sort(
      (a, b) => a.languagePosition - b.languagePosition
    )
  }, [])

  const setGridSelectedAvatarDimensionality = useSetRecoilState(
    gridSelectedAvatarDimensionalityAtom
  )
  const listLength: number = 0
  const isTouchDevice = checkIsTouchDevice()
  const { isOpen, onOpen, onClose } = useDisclosure()

  const isMobile = useIsMobileDevice()
  const [isSelected, setIsSelected] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)
  useClickAway(ref, () => setIsSelected(false))

  // don't show the hover overlay if touch device or the other room is joining

  const [, lsConfig] = useLocalStorage("avatarConfig")
  const isCurrentRoomLoading =
    avatarConfig?.avatarId === lsConfig?.avatarId && !!loadingRoomHash

  const handleOnClick = (dimensionality: AvatarDimensionality) => {
    setGridSelectedAvatarDimensionality(dimensionality)
    setGridSelectedAvatar(avatarConfig)

    // requesting device orientation permission
    !loadingRoomHash && roomHash && openRoom(roomHash)
  }

  const styling = isSelected
    ? {
        color: gridBranding.panelMouseoverTextColor,
        bg: gridBranding.panelMouseoverBackgroundColor,
        borderColor: gridBranding.panelMouseoverBorderColor
      }
    : {
        color: gridBranding.panelTextColor,
        background: gridBranding.panelBackgroundColor,
        borderColor: gridBranding.panelBorderColor
      }

  return (
    <Box pos="relative" overflow="hidden" outline="none" ref={ref}>
      <Flex
        width="100%"
        flexDir="column"
        padding="8px"
        borderRadius="8px"
        borderWidth="1px"
        _hover={
          !isMobile
            ? {
                color: gridBranding.panelMouseoverTextColor,
                bg: gridBranding.panelMouseoverBackgroundColor,
                borderColor: gridBranding.panelMouseoverBorderColor
              }
            : {}
        }
        {...styling}
        // onTouchStart={onTouchStart}
        // onTouchEnd={onTouchEnd}
      >
        <Flex alignItems="center">
          {/* <UserAvatar
                name={"avatar-name"}
                size={"xs"}
                src={branding.roomAvatarImage}
                colorDisk={"transparent"}
                avatar={null}
                avatarStyle={{ width: "1rem", height: "1rem" }}
                fontSize="0.6rem"
              /> */}
          <Text
            fontSize="0.8rem"
            marginInlineStart="0.3rem"
            fontWeight="500"
            noOfLines={1}
            wordBreak="break-all"
          >
            {avatarConfig.avatarName}
          </Text>

          {/*  Info icon */}
          <Box
            paddingX={"1"}
            onClick={event => {
              event.stopPropagation()
              onOpen()
            }}
            onTouchEnd={event => event.stopPropagation()}
            position="relative"
            {...addHitAreaStyle({ left: 6, right: 6, top: 8, bottom: 8 })}
            cursor="pointer"
          >
            <IoInformationCircleSharp fontSize="1.2rem" />
          </Box>
          <LanguageIcon languages={sortedLanguageList} />
        </Flex>

        <Flex
          grow={1}
          marginTop="8px"
          overflow="hidden"
          borderRadius="4px"
          style={{ aspectRatio: "9/16" }}
          position="relative"
          role="group" // added group for applying _groupHover to child
          onClick={() => {
            if (isMobile) setIsSelected(prevValue => !prevValue)
          }}
        >
          <Box position="relative">
            <Image
              objectFit="cover"
              src={avatarConfig.avatarPhoto}
              alt="Chakra UI"
            />
            {/* <Box position="absolute" top="4px" right="0">
              <LanguageIcon languages={sortedLanguageList} />
            </Box> */}
          </Box>
          <Box
            visibility={isMobile && isSelected ? "visible" : "hidden"}
            display="flex"
            position="absolute"
            top="0"
            left="0"
            width="100%"
            height="100%"
            _groupHover={
              !isMobile
                ? {
                    visibility: "visible"
                  }
                : {}
            }
          >
            {branding && (
              <AvatarCardHoverOverlay
                avatarConfig={avatarConfig}
                branding={branding}
                openShow={handleOnClick}
                btnText={btnText}
                openShowInNewTab={() => {}}
                isRoomloading={!!isCurrentRoomLoading}
                isCurrentRoomLoading={isCurrentRoomLoading}
              />
            )}
          </Box>
          {isCurrentRoomLoading && (
            <ShowCardLoaderOverlay
              color={
                branding?.authPageButtonMouseoverBackgroundColor ?? "#24A840"
              }
            />
          )}
        </Flex>
      </Flex>

      {branding && (
        <AvatarDescriptionModal
          background={branding.authPagePanelBackgroundColor}
          color={branding.authPagePanelTextColor}
          isOpen={isOpen}
          onClose={onClose}
          name={avatarConfig.avatarName}
          description={
            avatarConfig.avatarDescription ??
            "This <b>avatar</b> does not currently have a description."
          }
        />
      )}
    </Box>
  )
}
